import { useCallback } from 'react';
import { useDeleteAgent } from '@/hooks/use-delete-agent';
import { fetchWorldBootstrap } from '../../../lib/backend/world';
import { useToastStore } from '../../../lib/toast-store';
import useWorldStore from '../store/worldStore';

export default function useWorldDeleteAgent() {
  const removeAgent = useWorldStore((s) => s.removeAgent);
  const {
    handleDelete,
    getState,
    errorMessage,
  } = useDeleteAgent();
  const showToast = useToastStore((s) => s.showToast);
  const updateToast = useToastStore((s) => s.updateToast);

  const deleteAgentFromWorld = useCallback(async (agent) => {
    if (!agent?.id) return false;

    const toastId = `world-agent-delete-${agent.id}`;
    const displayName = agent.name || agent.id;

    showToast({
      id: toastId,
      message: `Deleting "${displayName}"…`,
      progress: 30,
    });

    let failureMessage = null;
    const result = await handleDelete(agent.id, {
      refresh: false,
      onError: (message) => {
        failureMessage = message;
      },
    });

    if (!result) {
      updateToast(toastId, {
        progress: undefined,
        message: `Delete failed: ${failureMessage || `Could not delete "${displayName}".`}`,
        variant: 'error',
      });
      return false;
    }

    removeAgent(agent.id);

    try {
      const bootstrap = await fetchWorldBootstrap();
      const store = useWorldStore.getState();
      store.applyBootstrap(bootstrap);
      store.setOpenclawConnected(Boolean(bootstrap?.openclaw_connected));
    } catch (err) {
      console.warn('Failed to refresh world bootstrap after delete:', err);
    }

    updateToast(toastId, {
      progress: 100,
      message: `Deleted "${displayName}".`,
    });

    return true;
  }, [handleDelete, removeAgent, showToast, updateToast]);

  return {
    deleteAgentFromWorld,
    getDeleteState: getState,
    deleteErrorMessage: errorMessage,
  };
}
